import { useRef, useState } from 'react'
import { IconUpload, IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import classes from './GenerationForm.module.css'

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp'

/**
 * Figma "File Upload": a 70px dashed drop area ("Click or Drag and Drop upload file here")
 * that turns into the list of attached files ("filename.jpg is uploaded ×") with an
 * "add more file" link until the limit is reached. Dropping works on both states.
 */
export function FileDropzone({ files, onAdd, onRemove, max = 3, disabled = false, label }) {
  const { t } = useTranslation()
  const input = useRef(null)
  const [over, setOver] = useState(false)
  const full = files.length >= max
  const blocked = disabled || full

  const pick = () => {
    if (!blocked) input.current?.click()
  }

  const dropHandlers = {
    onDragOver: (event) => {
      event.preventDefault()
      if (!blocked) setOver(true)
    },
    onDragLeave: () => setOver(false),
    onDrop: (event) => {
      event.preventDefault()
      setOver(false)
      if (!blocked) onAdd(event.dataTransfer.files)
    },
  }

  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        accept={ACCEPT}
        aria-label={label || t('editorV2.generation.files.upload')}
        hidden
        disabled={blocked}
        onChange={(event) => {
          onAdd(event.currentTarget.files)
          event.currentTarget.value = ''
        }}
      />
      {files.length === 0 ? (
        <button
          type="button"
          className={classes.dropzone}
          data-active={over ? 'true' : undefined}
          disabled={disabled}
          onClick={pick}
          {...dropHandlers}
        >
          <IconUpload size={16} stroke={1.8} aria-hidden="true" />
          <span>{t('editorV2.generation.files.dropPrompt')}</span>
        </button>
      ) : (
        <div className={classes.fileList} data-active={over ? 'true' : undefined} {...dropHandlers}>
          <div className={classes.fileNames}>
            {files.map((file, index) => (
              <span key={`${file.name}-${file.size}-${index}`} className={classes.fileName}>
                <span className={classes.fileText}>{t('editorV2.generation.files.uploaded', { name: file.name })}</span>
                <button
                  type="button"
                  className={classes.fileRemove}
                  aria-label={t('editorV2.generation.files.remove', { name: file.name })}
                  disabled={disabled}
                  onClick={() => onRemove(index)}
                >
                  <IconX size={14} stroke={1.8} />
                </button>
              </span>
            ))}
          </div>
          {!full && (
            <button type="button" className={classes.addMore} disabled={disabled} onClick={pick}>
              {t('editorV2.generation.files.addMore')}
            </button>
          )}
        </div>
      )}
    </>
  )
}
